import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import { useIsShoppingAssistantWebEnabled } from './useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantViewport } from './useShoppingAssistantViewport'

/**
 * Assisted-search experiment: for users assigned the `withAssistedSearch`
 * variation of the `experiment.assistedSearch` CloudBees experiment, pressing
 * Enter in the header search bar both navigates to the search results page AND
 * seeds a conversation in the assistant web sidebar.
 *
 * Gated by:
 * - `useIsShoppingAssistantWebEnabled` (assistant web feature must be on)
 * - `useShoppingAssistantViewport().isDesktop` (≥lg — panel + results visible side-by-side)
 * - `experiment.assistedSearch` === 'withAssistedSearch' (per-user assignment by the Experiments Worker)
 * - Sidebar closed OR open with no user messages (don't interrupt engaged users)
 *
 * Assignment is sticky per-user (CloudBees); the sidebar check is per-submit.
 */
export function useAssistedSearchExperiment() {
  const experiments = useExperiments()
  const isShoppingAssistantWebEnabled = useIsShoppingAssistantWebEnabled()
  const { isDesktop } = useShoppingAssistantViewport()
  const store = useShoppingAssistantWebSidebar()

  const isAssigned = computed(
    () =>
      isShoppingAssistantWebEnabled.value &&
      isDesktop.value &&
      experiments['experiment.assistedSearch'] === 'withAssistedSearch',
  )

  function shouldAssistSearch(): boolean {
    if (!isAssigned.value) return false
    if (store.isOpen && store.hasUserMessages) return false

    return true
  }

  return { shouldAssistSearch }
}
